/**
 * `<caos-page-pending>` — the page itself is waiting.
 * (Catalog: Status & feedback › PagePending — the state a page or route wears while it waits.)
 *
 * The THIRD busy case, and the only one that is about the page. `Button`'s loading state is busy on
 * the control doing the work; `SkeletonBlock` is busy on the region that is filling. Neither one
 * survives a route landed on from a link: an email-verification page reading "Confirming your
 * email…" has no button, because nobody clicked one, and no layout whose shape is worth preserving,
 * because there is no layout yet. The thing that is busy is the page.
 *
 * IT CANNOT BE PLACED. Its documentation declares `placements: []` — nowhere — so it appears in the
 * component reference and never in the palette. That is deliberate and load-bearing: a free-floating
 * mark an author drops onto a record page is still the wrong answer, and this must not become the
 * way to get one. The page or route that owns the wait sets `pending` on it in code.
 *
 * `pending` is the whole declaration. Absent, the element draws nothing, announces nothing and
 * occupies no space, so a surface can mount it once and toggle rather than build and discard it.
 *
 * `size` comes from the platform size scale (`xs`…`xl`, CAOS-586/594), not from names private to
 * this component: the mark is drawn in an icon's box, which is one of the three things that scale
 * exists for. Colour comes from `currentColor` — the layer it sits on — so it is legible on a dark
 * hero and on a white card without an author remembering to set anything.
 *
 * Styling seams: `::part(container)`, `::part(mark)`, `::part(label)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { sizeRules, type SizeStep } from '../sizes.js';

/** The step the mark is drawn at when `size` is absent or names nothing. */
export const PAGE_PENDING_DEFAULT_SIZE: SizeStep = 'lg';

/** What the live region says when the page named nothing. Matches the host's own vocabulary. */
export const PAGE_PENDING_DEFAULT_LABEL = 'Loading…';

/**
 * The pending state's own styles, as WRITTEN. Exported the way the icon exports `ICON_STYLES`,
 * and for the same reason: a claim about a rule here is a claim about what was authored, and a
 * test that reads it back off a parsed stylesheet is reading a serialiser's shorthand folding
 * instead. `border-top-color: transparent` comes back as a three-value `border-color` list, which
 * is the same rule and a different string.
 */
export const PAGE_PENDING_STYLES = `
  :host { display: block; }
  /* Not pending is not "invisible": it takes no space, so a mounted-and-idle instance cannot
     push the content that arrived down the page. */
  :host(:not([pending])) { display: none; }

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--caos-space-3);
    padding: var(--caos-space-6) var(--caos-space-5);
    text-align: center;
    /* currentColor, not a token: the mark and the line under it take the colour of whatever layer
       this sits on — a branded hero, a white card, a dark shell — with nothing to set. */
    color: currentColor;
  }

  .mark {
    display: block;
    border-radius: 50%;
    /* An INCOMPLETE ring. Three quarters of a border, one quarter transparent: the shape says
       unfinished whether or not it is turning, which is what carries the reduced-motion case. */
    border: 2px solid currentColor;
    border-top-color: transparent;
    opacity: 0.85;
    animation: caos-page-pending-turn 0.9s linear infinite;
  }

  ${sizeRules(PAGE_PENDING_DEFAULT_SIZE, (host, length) => `${host} .mark { width: ${length}; height: ${length}; }`)}

  .label {
    font-family: var(--caos-font);
    font-size: 13px;
    line-height: 1.5;
    color: currentColor;
    opacity: 0.8;
  }
  .label:empty { display: none; }

  @keyframes caos-page-pending-turn { to { transform: rotate(360deg); } }

  /* Reduced motion — the ring stops, and nothing else changes. It is still three quarters of a
     circle, which reads as unfinished rather than as an ornament, and the line under it says so in
     words. The progress bar's static striped track is the same trade. */
  @media (prefers-reduced-motion: reduce) {
    .mark { animation: none; }
  }
`;

const sheet = makeSheet(PAGE_PENDING_STYLES);

export class CaosPagePending extends CaosElement {
  static get observedAttributes(): string[] {
    return ['pending', 'label'];
  }

  private readonly labelEl: HTMLDivElement;

  constructor() {
    super(sheet);

    const container = document.createElement('div');
    container.className = 'container';
    container.setAttribute('part', 'container');

    const mark = document.createElement('div');
    mark.className = 'mark';
    mark.setAttribute('part', 'mark');
    // The ring is decoration. Everything a reader needs is in the live region below it, and a
    // second announcement of the same fact is worse than none.
    mark.setAttribute('aria-hidden', 'true');

    this.labelEl = document.createElement('div');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');
    // `role="status"` is a polite live region. Text arriving in it is announced; text LEAVING it is
    // what clears the announcement, which is why resolving empties this node rather than merely
    // hiding it or stopping the animation.
    this.labelEl.setAttribute('role', 'status');

    container.append(mark, this.labelEl);
    this.root.append(container);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    if (this.hasAttribute('pending')) {
      this.setAttribute('aria-busy', 'true');
      // An empty `label=""` is a page asking for the default rather than for silence: a pending
      // state nobody can hear is the thing this criterion exists to prevent.
      this.labelEl.textContent = this.getAttribute('label') || PAGE_PENDING_DEFAULT_LABEL;
    } else {
      this.removeAttribute('aria-busy');
      this.labelEl.textContent = '';
    }
  }
}

define('caos-page-pending', CaosPagePending);

declare global {
  interface HTMLElementTagNameMap {
    'caos-page-pending': CaosPagePending;
  }
}
